/**
 * Centralized, typed access to environment configuration.
 * Prefer importing `env` from here instead of reading `import.meta.env` directly.
 *
 * API 地址对齐 org-console：
 * - `.env.*` 只配真实后端 origin（`VITE_API_BASE_URL` / `VITE_ORG_API_BASE_URL`）
 * - 本地 `import.meta.env.DEV`：浏览器打同源 `/api`、`/org-api/v1`，由 Vite proxy 转到上述 origin
 * - 构建产物：直连 origin（org 再拼 `/api/v1`）
 */

function stripTrailingSlash(url: string): string {
  return url.replace(/\/+$/, '')
}

const apiOrigin = stripTrailingSlash(
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim() || '',
)
const orgApiOrigin = stripTrailingSlash(
  (import.meta.env.VITE_ORG_API_BASE_URL as string | undefined)?.trim() || '',
)

const isViteDev = import.meta.env.DEV

export const env = {
  appName: import.meta.env.VITE_APP_NAME,
  env: import.meta.env.VITE_ENV,
  /** 开发者 API 真实 origin（无 path）；cors / 文档用 */
  apiOrigin,
  /** 空间 org-server 真实 origin（无 `/api/v1`） */
  orgApiOrigin,
  /**
   * fetch baseURL（开发者业务）。
   * 本地：`/api`；构建：`VITE_API_BASE_URL`。
   */
  apiBaseUrl: isViteDev ? '/api' : apiOrigin,
  /**
   * fetch baseURL（org-server）。
   * 本地：`/org-api/v1`；构建：`{VITE_ORG_API_BASE_URL}/api/v1`。
   */
  orgApiBaseUrl: isViteDev
    ? '/org-api/v1'
    : orgApiOrigin
      ? `${orgApiOrigin}/api/v1`
      : '',
  /** 退出登录后跳转的 IM 登录页；未配置时回落到 `/` */
  imLoginUrl: (import.meta.env.VITE_IM_LOGIN_URL as string | undefined)?.trim() || '/',
  /** Hydra 授权地址，如 `https://…/oauth2/auth` */
  authUrl: (import.meta.env.VITE_AUTH_URL as string | undefined)?.trim() || '',
  /** 开发者平台独立 OAuth client，与 org-console 的 `org-console` 分开 */
  oauthClientId:
    (import.meta.env.VITE_OAUTH_CLIENT_ID as string | undefined)?.trim() ||
    'developer-console',
  oauthScope:
    (import.meta.env.VITE_OAUTH_SCOPE as string | undefined)?.trim() ||
    'openid offline_access',
  /**
   * `dev`：本地不强制走 Hydra。
   * 未设置时，只要配了 `VITE_DEV_AUTHORIZATION` 同样视为本地旁路。
   */
  authMode: (import.meta.env.VITE_AUTH_MODE as string | undefined)?.trim() || '',
  /** 本地联调鉴权（`.env.development.local`）。正式 JWT 缺失时 HTTP 仍回落到这里。 */
  devOrgId: import.meta.env.VITE_DEV_ORG_ID as string | undefined,
  devAuthorization: import.meta.env.VITE_DEV_AUTHORIZATION as string | undefined,
  isDev: import.meta.env.VITE_ENV === 'development',
  isBeta: import.meta.env.VITE_ENV === 'beta',
  isProd: import.meta.env.VITE_ENV === 'production',
} as const
